import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const QC_ITEMS = [
  { key: "audio", label: "Audio", desc: "Levels balanced, no clipping" },
  { key: "video", label: "Video", desc: "Color, exposure, framing pass" },
  { key: "pacing", label: "Pacing", desc: "Cuts feel tight, no drag" },
  { key: "graphics", label: "Graphics", desc: "Lower thirds, supers accurate" },
  { key: "format", label: "Format", desc: "Aspect, codec, bitrate correct" },
  { key: "duration", label: "Duration", desc: "Within agreed length" },
  { key: "naming", label: "Naming", desc: "File names match spec" },
  { key: "watermark", label: "Watermark", desc: "Removed for delivery" },
];

const STAGES = ["input", "process", "output", "closure"];

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  const load = async () => setP(await api.getProject(id));
  useEffect(() => { load(); }, [id]);

  const update = async (patch) => {
    try {
      const next = await api.updateProject(id, patch);
      setP(next);
    } catch { toast.error("Update failed"); }
  };

  const toggleQc = (key) => update({ qc: { ...p.qc, [key]: !p.qc[key] } });
  const qcDone = p ? QC_ITEMS.filter(i => p.qc[i.key]).length : 0;

  if (!p) return <div className="mono text-white/50">LOADING…</div>;

  return (
    <div className="space-y-8" data-testid={`project-detail-${id}`}>
      <Link to="/app/factory/projects" className="mono text-white/50 hover:text-kinetic inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> ALL PROJECTS</Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-white/40">{p.client_name}</div>
          <h1 className="text-4xl font-extrabold mt-1">{p.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          <Select value={p.stage} onValueChange={(v) => update({ stage: v })}>
            <SelectTrigger data-testid="stage-select" className="w-44 bg-black border-white/15 rounded-sm mono"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0C0C0C] border-white/15 text-white">
              {STAGES.map(s => <SelectItem key={s} value={s} className="mono">{s.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-[#0C0C0C] border border-white/10 rounded-sm">
          <TabsTrigger value="overview" data-testid="tab-overview" className="mono data-[state=active]:bg-kinetic data-[state=active]:text-black rounded-sm">Overview</TabsTrigger>
          <TabsTrigger value="qc" data-testid="tab-qc" className="mono data-[state=active]:bg-kinetic data-[state=active]:text-black rounded-sm">QC · 8-POINT</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="mono data-[state=active]:bg-kinetic data-[state=active]:text-black rounded-sm">Milestones</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 border border-white/10 bg-[#0C0C0C] p-6 space-y-4">
              <div>
                <Label className="mono text-white/60">Title</Label>
                <Input data-testid="edit-title" value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} onBlur={() => update({ title: p.title })} className="bg-black border-white/15 rounded-sm mt-1" />
              </div>
              <div>
                <Label className="mono text-white/60">Client</Label>
                <Input value={p.client_name} onChange={(e) => setP({ ...p, client_name: e.target.value })} onBlur={() => update({ client_name: p.client_name })} className="bg-black border-white/15 rounded-sm mt-1" />
              </div>
              <div>
                <Label className="mono text-white/60">Description</Label>
                <Textarea data-testid="edit-description" value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} onBlur={() => update({ description: p.description })} className="bg-black border-white/15 rounded-sm mt-1 min-h-32" />
              </div>
            </div>
            <div className="border border-white/10 bg-[#0C0C0C] p-6 space-y-4">
              <div>
                <div className="mono text-white/40">QC PROGRESS</div>
                <div className="font-sans text-3xl font-extrabold mt-1">{qcDone}/8</div>
                <div className="h-1 bg-white/5 mt-3"><div className="h-full bg-kinetic" style={{ width: `${(qcDone/8)*100}%` }}/></div>
              </div>
              <div>
                <div className="mono text-white/40">PAID</div>
                <div className="font-sans text-3xl font-extrabold mt-1">₹{(p.paid || 0).toLocaleString('en-IN')}<span className="text-white/30 text-base font-normal"> / ${(p.budget||0).toLocaleString('en-IN')}</span></div>
              </div>
              <div>
                <Label className="mono text-white/60">Budget</Label>
                <Input type="number" value={p.budget} onChange={(e) => setP({ ...p, budget: Number(e.target.value) })} onBlur={() => update({ budget: p.budget })} className="bg-black border-white/15 rounded-sm mt-1" />
              </div>
              <div>
                <Label className="mono text-white/60">Paid to date</Label>
                <Input data-testid="edit-paid" type="number" value={p.paid} onChange={(e) => setP({ ...p, paid: Number(e.target.value) })} onBlur={() => update({ paid: p.paid })} className="bg-black border-white/15 rounded-sm mt-1" />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="qc" className="mt-6">
          <div className="border border-white/10 bg-[#0C0C0C]">
            <div className="px-5 py-3 border-b border-white/10 mono text-white/60 flex justify-between">
              <span>QC · FLIGHT CHECK</span><span>{qcDone}/8 PASS</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-y divide-white/10">
              {QC_ITEMS.map(item => (
                <div key={item.key} className="p-5 flex items-center justify-between gap-4">
                  <div>
                    <div className="font-sans font-bold text-lg">{item.label}</div>
                    <div className="mono text-white/40 mt-1">{item.desc}</div>
                  </div>
                  <Switch data-testid={`qc-toggle-${item.key}`} checked={p.qc[item.key]} onCheckedChange={() => toggleQc(item.key)} />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <div className="border border-white/10 bg-[#0C0C0C]">
            <div className="px-5 py-3 border-b border-white/10 mono text-white/60">MILESTONES · 50 / 25 / 25</div>
            <div className="divide-y divide-white/10">
              {[
                ["milestone_advance", "50%", "Advance · triggers production"],
                ["milestone_mid", "25%", "Milestone · first cut delivered"],
                ["milestone_final", "25%", "Final · approved & delivered"],
              ].map(([k, pct, desc]) => (
                <div key={k} className="grid grid-cols-[80px_1fr_auto] items-center gap-4 px-5 py-5">
                  <div className="font-sans text-3xl font-extrabold text-kinetic">{pct}</div>
                  <div>
                    <div className="font-sans font-bold">{desc}</div>
                    <div className="mono text-white/40 mt-1">{p[k] ? "PAID" : "PENDING"}</div>
                  </div>
                  <Switch data-testid={`milestone-${k}`} checked={p[k]} onCheckedChange={(v) => update({ [k]: v })} />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
